import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  AssignableCourier,
  AssignmentPreview,
  BouquetBuilderItem,
  Color,
  CustomerOrder,
  CustomerOrderLinePayload,
  DeliveryJob,
  PaperRollRow,
  OrderableItem,
  SubCategory,
} from "../api/types";
import { eur } from "../components/bouquet/builderShared";
import {
  EMPTY_CART,
  aggregateRolls,
  cartIsEmpty,
  cartStemCount,
  cartSuggestedPrice,
  cartToOrderLines,
  deliveryLegPlan,
  emptyCheckoutDraft,
  goodsTotal,
  parseShortages,
  retailLineFromItem,
  retailToOrderLines,
  validateCheckout,
  type ComposerCart,
  type RetailOrderLine,
  type StockShortage,
  orderSourceLabel,
} from "../components/orders/orderBuild";
import {
  deliveryJobStatusLabel,
  fulfillmentLabel,
  orderDayKey,
} from "../components/orders/orderUi";
import { t } from "../i18n/t";
import { resizeImageFile } from "../utils/resizeImage";
import {
  DescribeBouquet,
  type DescribePhotoThumb,
} from "../components/orders/DescribeBouquet";
import { PhoneBouquetComposer } from "../components/orders/PhoneBouquetComposer";
import { PhoneOrderCheckout } from "../components/orders/PhoneOrderCheckout";
import { PhoneOrderItems } from "../components/orders/PhoneOrderItems";
import { localIsoFromDateTime } from "../components/tasks/taskUi";
import { useAuth } from "../state/AuthContext";
import type { DashboardOutletContext } from "./dashboardOutletContext";

type Step = "compose" | "checkout" | "items" | "done";
/** G-15: how the bouquet enters the order — described in text, or composed. */
type BuilderMode = "describe" | "compose";

const REFERENCE_PHOTO_CAP = 10;
/** Longest edge kept for reference photos — the server caps uploads at 15 MB. */
const REFERENCE_PHOTO_MAX_DIM = 1024;

interface PhotoDraft {
  key: string;
  file: File;
  previewUrl: string;
}

interface SendResult {
  order: CustomerOrder;
  job: DeliveryJob | null;
  jobError: string | null;
  assignedCourierName: string | null;
  assignError: string | null;
  photoError: string | null;
}

function localTimeFromIso(value: string | null): string {
  if (!value) return "12:00";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "12:00";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * Phone "Build & send order": a manager/owner away from the shop composes a
 * bouquet from a location's live stock, attaches customer + fulfillment
 * details, and sends it to that location's order list (plan:
 * bouquetbuilderformanagers.md). No stock is consumed here — the server
 * validates availability and the florist builds on the floor.
 */
export function OrderBuildPage() {
  const { locations } = useOutletContext<DashboardOutletContext>();
  const [searchParams] = useSearchParams();
  const routeParams = useParams<{ orderId?: string }>();
  const editingOrderId = routeParams.orderId ?? searchParams.get("orderId") ?? "";
  const { user, hasRole } = useAuth();
  const ownerScoped = hasRole("OWNER");

  const activeLocations = useMemo(
    () => locations.filter((l) => l.isActive),
    [locations],
  );
  const [pickedLocationId, setPickedLocationId] = useState(
    () => searchParams.get("locationId") ?? "",
  );
  const locationId = useMemo(() => {
    if (ownerScoped) {
      if (pickedLocationId && activeLocations.some((l) => l.id === pickedLocationId)) {
        return pickedLocationId;
      }
      return "";
    }
    return user?.assignedLocation?.id ?? "";
  }, [ownerScoped, pickedLocationId, activeLocations, user?.assignedLocation?.id]);
  const locationName = useMemo(() => {
    if (!ownerScoped) return user?.assignedLocation?.name ?? "your shop";
    return activeLocations.find((l) => l.id === locationId)?.name ?? "the selected shop";
  }, [ownerScoped, user?.assignedLocation?.name, activeLocations, locationId]);

  // ── Catalog data for the composer ──────────────────────────────────────────
  const [flowers, setFlowers] = useState<BouquetBuilderItem[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [rolls, setRolls] = useState<PaperRollRow[]>([]);
  const [orderableItems, setOrderableItems] = useState<OrderableItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadCatalog = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [flowerRes, colorRes, subRes, rollRes, itemRes] = await Promise.all([
        api.listBouquetBuilderItems(locationId),
        api.listColors().catch(() => [] as Color[]),
        api.listSubCategories("FLOWER").catch(() => [] as SubCategory[]),
        api.listPaperRolls(locationId).catch(() => [] as PaperRollRow[]),
        api.listOrderableItemsInStock(locationId),
      ]);
      setFlowers(flowerRes);
      setColors(colorRes);
      setSubcategories(subRes);
      setRolls(rollRes);
      setOrderableItems(itemRes);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : "Could not load the shop's stock");
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (document.visibilityState === "visible") void loadCatalog();
    };
    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [loadCatalog]);

  // ── Flow state ─────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>("compose");
  const [cart, setCart] = useState<ComposerCart>(EMPTY_CART);
  const [retailLines, setRetailLines] = useState<RetailOrderLine[]>([]);
  // The orders calendar deep-links "+ New order for this day" as ?day=; a
  // valid date wins over today (owner report 2026-07-29). Past days clamp to
  // today — orders are never created in the past (owner decision same day).
  const [draft, setDraft] = useState(() => {
    const requestedDay = searchParams.get("day");
    const today = orderDayKey(new Date());
    return emptyCheckoutDraft(
      requestedDay &&
        /^\d{4}-\d{2}-\d{2}$/.test(requestedDay) &&
        requestedDay >= today
        ? requestedDay
        : today,
    );
  });
  /*
   * G-15 chose the mode up front on a screen of its own, and once chosen there
   * was no way back without leaving the page. G-93 makes it a switch that stays
   * on screen, and starts on DESCRIBE — the owner's call, and the common case:
   * most orders arrive as "something pastel for a 60th", not as a stem list.
   * Edit mode still derives the mode from whether the order has composed lines.
   */
  const [mode, setMode] = useState<BuilderMode>("describe");
  // Up to 10 reference photos: new drafts + already-saved ones (edit mode).
  const [photoDrafts, setPhotoDrafts] = useState<PhotoDraft[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<DescribePhotoThumb[]>([]);
  const [removePhotoIds, setRemovePhotoIds] = useState<string[]>([]);
  const [editingOrder, setEditingOrder] = useState<CustomerOrder | null>(null);
  const [preservedLines, setPreservedLines] = useState<CustomerOrderLinePayload[]>([]);
  // G-112: held ready-made bouquets are priced goods — the agreed total an
  // edit sends must keep counting them.
  const heldBouquetsEur = preservedLines.reduce(
    (sum, line) =>
      line.role === "READY_BOUQUET" ? sum + (line.unitPriceEur ?? 0) : sum,
    0,
  );
  const [editLoading, setEditLoading] = useState(Boolean(editingOrderId));
  const [editInitialized, setEditInitialized] = useState(false);
  const [shortages, setShortages] = useState<StockShortage[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);

  useEffect(() => {
    if (!editingOrderId) return;
    let stale = false;
    setEditLoading(true);
    Promise.all([
      api.getCustomerOrder(editingOrderId),
      api.getCustomerOrder(editingOrderId).then((order) =>
        order.deliveryJob ? api.getDeliveryJob(order.deliveryJob.id) : null,
      ),
    ])
      .then(async ([order, delivery]) => {
        if (stale) return;
        if (order.status !== "NEW" && order.status !== "CONFIRMED") {
          throw new Error("This order is read-only because preparation has already started.");
        }
        setEditingOrder(order);
        setPickedLocationId(order.location.id);
        setMode(
          order.lines.some((line) => line.role === "BOUQUET_COMPONENT")
            ? "compose"
            : "describe",
        );
        setDraft({
          ...emptyCheckoutDraft(order.scheduledDate.slice(0, 10)),
          // G-56: a staff source round-trips; an adapter source (Shopify,
          // Glovo…) edited here is shown as "Other".
          source:
            order.source === "PHONE" ||
            order.source === "WHATSAPP" ||
            order.source === "INSTAGRAM"
              ? order.source
              : "MANUAL",
          customerName: order.customerName,
          customerPhone: order.customerPhone ?? "",
          customerEmail: order.customerEmail ?? "",
          customerNotes: order.customerNotes ?? "",
          orderType: order.orderType === "business" ? "business" : "personal",
          fulfillment: order.fulfillment === "PICKUP" ? "PICKUP" : "COURIER",
          timeType: order.timeType,
          date: order.scheduledDate.slice(0, 10),
          timeFrom: localTimeFromIso(order.scheduledFor),
          timeUntil: localTimeFromIso(order.scheduledUntil),
          prepaid: Boolean(order.prepaidAt),
          deliveryAddress: delivery?.deliveryAddress ?? "",
          recipientName: delivery?.recipientName ?? "",
          recipientPhone: delivery?.recipientPhone ?? "",
          recipientNotes: delivery?.recipientNotes ?? "",
          deliveryPriceEur: delivery?.priceEur ?? "",
          packageSize: delivery?.packageSize ?? "",
          distanceKm: delivery?.distanceKm ?? "",
          overridePayoutEur: "",
          courierUserId: delivery?.courier?.id ?? "",
        });
        setCart((current) => ({
          ...current,
          bouquetName: order.bouquetName ?? "",
          bouquetNotes: order.bouquetNotes ?? "",
          bouquetPriceEur: order.bouquetPriceEur ?? "",
        }));
        // Lines this editor does not own ride through an edit untouched:
        // legacy GENERAL text, and (G-112) the ready-made bouquets the order
        // holds — dropping those would release them back to walk-in stock.
        setPreservedLines(
          order.lines
            .filter(
              (line) => line.role === "GENERAL" || line.role === "READY_BOUQUET",
            )
            .map((line) => ({
              description: line.description,
              itemId: line.itemId ?? undefined,
              bouquetTemplateId: line.bouquetTemplateId ?? undefined,
              bouquetBuildId: line.bouquetBuild?.id,
              quantity: Number(line.quantity),
              unitPriceEur:
                line.unitPriceEur != null ? Number(line.unitPriceEur) : undefined,
              role: line.role,
            })),
        );
        if (order.referencePhotoCount > 0) {
          const photos = await api.listOrderReferencePhotos(order.id);
          const thumbs = await Promise.all(
            photos.map(async (photo) => ({
              key: photo.id,
              previewUrl: await api.fetchOrderReferencePhotoBlobUrl(
                order.id,
                photo.id,
              ),
            })),
          );
          const resolved = thumbs.filter(
            (thumb): thumb is DescribePhotoThumb => thumb.previewUrl != null,
          );
          if (stale) {
            // Navigated away (or StrictMode's double-mount) — these URLs
            // would otherwise be pinned for the life of the page.
            resolved.forEach((thumb) => URL.revokeObjectURL(thumb.previewUrl));
            return;
          }
          setExistingPhotos(resolved);
        }
      })
      .catch((err: unknown) => {
        if (!stale) {
          setLoadError(err instanceof Error ? err.message : "Could not load the order.");
        }
      })
      .finally(() => {
        if (!stale) setEditLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [editingOrderId]);

  useEffect(() => {
    if (!editingOrder || loading || editInitialized) return;
    const rollByItem = new Map(aggregateRolls(rolls).map((roll) => [roll.itemId, roll]));
    const bouquetFlowers: ComposerCart["flowers"] = [];
    const bouquetPapers: ComposerCart["papers"] = [];
    const nextRetail: RetailOrderLine[] = [];

    for (const line of editingOrder.lines) {
      const qty = Number(line.quantity);
      if (line.role === "BOUQUET_COMPONENT" && line.itemId) {
        const roll = rollByItem.get(line.itemId);
        if (roll) {
          bouquetPapers.push({
            itemId: line.itemId,
            name: line.itemName ?? line.description,
            categoryLabel: roll.categoryLabel,
            cm: qty,
            availableCm: Math.max(qty, roll.availableCm),
          });
        } else {
          const item = flowers.find((candidate) => candidate.itemId === line.itemId);
          bouquetFlowers.push({
            itemId: line.itemId,
            name: line.itemName ?? line.description,
            imageKey: item?.imageKey ?? null,
            qty,
            maxQty: Math.max(qty, Number(item?.qtyOnHand ?? 0)),
            unitSalePrice:
              line.unitPriceEur != null
                ? Number(line.unitPriceEur)
                : item?.defaultSaleUnitPriceEur != null
                  ? Number(item.defaultSaleUnitPriceEur)
                  : null,
          });
        }
      }
      if (line.role === "RETAIL_ITEM" && line.itemId) {
        const item = orderableItems.find((candidate) => candidate.id === line.itemId);
        if (item) {
          nextRetail.push({
            ...retailLineFromItem(item),
            qty,
            maxQty: Math.max(qty, Number(item.qtyOnHand)),
            unitSalePrice:
              line.unitPriceEur != null
                ? Number(line.unitPriceEur)
                : item.defaultSaleUnitPriceEur != null
                  ? Number(item.defaultSaleUnitPriceEur)
                  : null,
            unavailable: Number(item.qtyOnHand) < qty,
          });
        } else {
          nextRetail.push({
            itemId: line.itemId,
            name: line.itemName ?? line.description,
            imageKey: null,
            categoryId: "unavailable",
            categoryLabel: "Unavailable",
            colorId: null,
            colorName: null,
            qty,
            maxQty: qty,
            unitSalePrice:
              line.unitPriceEur != null ? Number(line.unitPriceEur) : null,
            unavailable: true,
          });
        }
      }
    }
    setCart((current) => ({
      ...current,
      flowers: bouquetFlowers,
      papers: bouquetPapers,
    }));
    setRetailLines(nextRetail);
    setEditInitialized(true);
  }, [editingOrder, editInitialized, loading, flowers, rolls, orderableItems]);

  // ── G-15 reference-photo helpers (drafts + saved, shared by both modes) ────
  const describePhotos: DescribePhotoThumb[] = [
    ...existingPhotos,
    ...photoDrafts.map((draft) => ({ key: draft.key, previewUrl: draft.previewUrl })),
  ];

  /**
   * Every reference photo enters here (describe grid AND composer picker), so
   * this is the one place to downscale. A raw phone shot easily exceeds the
   * server's 15 MB cap — resize first, exactly as PhotoInput does for item
   * photos, then draft it.
   */
  const addPhotoFiles = async (files: File[]) => {
    const room = REFERENCE_PHOTO_CAP - describePhotos.length;
    if (room <= 0) return;
    const prepared = await Promise.all(
      files.slice(0, room).map(async (file) => {
        const resized = await resizeImageFile(file, REFERENCE_PHOTO_MAX_DIM).catch(
          () => file, // a resize failure must never block sending the order
        );
        return {
          key: `draft-${crypto.randomUUID()}`,
          file: resized,
          previewUrl: URL.createObjectURL(resized),
        };
      }),
    );
    setPhotoDrafts((cur) => [...cur, ...prepared]);
  };

  const removePhoto = (key: string) => {
    const draft = photoDrafts.find((candidate) => candidate.key === key);
    if (draft) {
      URL.revokeObjectURL(draft.previewUrl);
      setPhotoDrafts((cur) => cur.filter((candidate) => candidate.key !== key));
      return;
    }
    const saved = existingPhotos.find((candidate) => candidate.key === key);
    if (saved) URL.revokeObjectURL(saved.previewUrl);
    setExistingPhotos((cur) => cur.filter((candidate) => candidate.key !== key));
    setRemovePhotoIds((cur) => (cur.includes(key) ? cur : [...cur, key]));
  };

  const clearAllPhotos = () => {
    photoDrafts.forEach((draft) => URL.revokeObjectURL(draft.previewUrl));
    existingPhotos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    setPhotoDrafts([]);
    setRemovePhotoIds((cur) => [
      ...cur,
      ...existingPhotos.map((photo) => photo.key).filter((key) => !cur.includes(key)),
    ]);
    setExistingPhotos([]);
  };

  const changeLocation = (nextId: string) => {
    if (nextId === locationId) return;
    if (
      !cartIsEmpty(cart) &&
      !window.confirm("Changing shop clears the bouquet (stock differs per shop). Continue?")
    ) {
      return;
    }
    setCart(EMPTY_CART);
    setRetailLines([]);
    photoDrafts.forEach((draft) => URL.revokeObjectURL(draft.previewUrl));
    setPhotoDrafts([]);
    setShortages(null);
    setPickedLocationId(nextId);
  };

  // G-177 (KI-42, KI-43): one answer for the form, its checks and the save.
  const deliveryPlan = deliveryLegPlan(editingOrder, draft.fulfillment);

  // ── Couriers (lazy — only needed on the checkout step for deliveries) ──────
  const [couriers, setCouriers] = useState<AssignableCourier[]>([]);
  const [couriersLoaded, setCouriersLoaded] = useState(false);
  const [couriersLoading, setCouriersLoading] = useState(false);
  useEffect(() => {
    if (
      step !== "checkout" ||
      !deliveryPlan.mayAssignCourier ||
      couriersLoaded ||
      couriersLoading
    ) return;
    setCouriersLoading(true);
    api
      .listAssignableCouriers()
      .then(setCouriers)
      .catch(() => setCouriers([]))
      .finally(() => {
        setCouriersLoaded(true);
        setCouriersLoading(false);
      });
  }, [step, deliveryPlan.mayAssignCourier, couriersLoaded, couriersLoading]);

  const [payoutPreview, setPayoutPreview] = useState<AssignmentPreview | null>(null);
  const [payoutPreviewLoading, setPayoutPreviewLoading] = useState(false);
  const [payoutPreviewError, setPayoutPreviewError] = useState<string | null>(null);
  useEffect(() => {
    const priceEur = Number(draft.deliveryPriceEur);
    if (
      step !== "checkout" ||
      !deliveryPlan.mayAssignCourier ||
      !draft.courierUserId ||
      !locationId ||
      !draft.deliveryPriceEur.trim() ||
      !Number.isFinite(priceEur) ||
      priceEur < 0
    ) {
      setPayoutPreview(null);
      setPayoutPreviewError(null);
      setPayoutPreviewLoading(false);
      return;
    }

    let stale = false;
    setPayoutPreviewLoading(true);
    setPayoutPreviewError(null);
    const timer = window.setTimeout(() => {
      const distanceKm = draft.distanceKm.trim()
        ? Number(draft.distanceKm)
        : undefined;
      api
        .previewDeliveryPayout({
          locationId,
          courierUserId: draft.courierUserId,
          priceEur,
          distanceKm:
            distanceKm != null && Number.isFinite(distanceKm)
              ? distanceKm
              : undefined,
        })
        .then((preview) => {
          if (!stale) setPayoutPreview(preview);
        })
        .catch((err: unknown) => {
          if (!stale) {
            setPayoutPreview(null);
            setPayoutPreviewError(
              err instanceof Error ? err.message : "Could not calculate courier payout.",
            );
          }
        })
        .finally(() => {
          if (!stale) setPayoutPreviewLoading(false);
        });
    }, 300);

    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [
    step,
    deliveryPlan.mayAssignCourier,
    draft.courierUserId,
    draft.deliveryPriceEur,
    draft.distanceKm,
    locationId,
  ]);

  // ── Submit orchestration ───────────────────────────────────────────────────
  const submit = async () => {
    const problem = validateCheckout(draft, deliveryPlan.deliveryDetails);
    if (problem) {
      setSubmitError(problem);
      setStep("checkout");
      return;
    }
    // G-177 (KI-42): a courier already holding the job is not re-assigned.
    const assigningCourier = deliveryPlan.mayAssignCourier && draft.courierUserId !== "";
    // G-23: checkout is now the LAST step, so the courier payout guards that
    // used to run at the old step boundary run here — otherwise a courier
    // order could be sent with an unresolved payout.
    if (assigningCourier && payoutPreviewLoading) {
      setSubmitError("Wait a moment while the courier payout is calculated.");
      return;
    }
    if (assigningCourier && payoutPreviewError) {
      setSubmitError("Resolve the courier payout error before sending the order.");
      return;
    }
    if (
      assigningCourier &&
      payoutPreview?.requiresDistance &&
      !draft.distanceKm.trim()
    ) {
      setSubmitError("This courier's payout rule requires the delivery distance.");
      return;
    }
    if (
      assigningCourier &&
      payoutPreview &&
      !payoutPreview.hasRule &&
      (!draft.overridePayoutEur.trim() ||
        !Number.isFinite(Number(draft.overridePayoutEur)) ||
        Number(draft.overridePayoutEur) < 0)
    ) {
      setSubmitError("Enter the courier payout because no active rule applies.");
      return;
    }
    if (assigningCourier && !payoutPreview) {
      setSubmitError("Wait for the courier payout preview before continuing.");
      return;
    }
    /*
     * G-93: the MODE decides whether composed stems are part of the order.
     *
     * Until this branch the question was asked once and could not be revisited,
     * so the cart could only ever be filled in the mode being submitted. With a
     * two-way switch you can pick twelve roses, change your mind, describe the
     * bouquet instead — and the roses would have gone anyway as stock-linked
     * lines that reserve stock and price the bouquet, with nothing on the
     * describe screen to say so. Found by /code-review before the merge.
     *
     * Nothing is thrown away: the cart is kept, so switching back restores the
     * stems, and the describe screen says they are waiting there.
     */
    const bouquetLines = mode === "compose" ? cartToOrderLines(cart) : [];
    const itemLines = retailToOrderLines(retailLines);
    const lines = [...preservedLines, ...bouquetLines, ...itemLines];
    // G-15: a lineless order is legal when it carries a bouquet description —
    // the server accepts zero lines only alongside bouquet text.
    const describeTextPresent =
      cart.bouquetNotes.trim().length > 0 || cart.bouquetName.trim().length > 0;
    if (lines.length === 0 && !describeTextPresent) {
      setSubmitError(
        "Describe the bouquet or add at least one shop item before sending.",
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    // G-15 (owner, 2026-07-31): the order stores the GOODS total — agreed
    // bouquet price + shop items. Delivery is priced on the DeliveryJob.
    const bouquetPrice = cart.bouquetPriceEur.trim()
      ? Number(cart.bouquetPriceEur)
      : undefined;
    const goods = goodsTotal(cart.bouquetPriceEur, retailLines, heldBouquetsEur);
    const agreedTotal = goods ?? undefined;

    let order: CustomerOrder;
    try {
      const shared = {
        customerName: draft.customerName.trim(),
        customerPhone: draft.customerPhone.trim() || undefined,
        customerEmail: draft.customerEmail.trim() || undefined,
        customerNotes: draft.customerNotes.trim() || undefined,
        // G-15: the text is meaningful with OR without composed lines (the
        // describe mode has none); Skip-bouquet clears the cart fields, so
        // sending trim-or-null preserves the old skip semantics too.
        bouquetName: cart.bouquetName.trim() || null,
        bouquetNotes: cart.bouquetNotes.trim() || null,
        orderType: draft.orderType,
        // G-177 (KI-43): an external-courier order keeps its own fulfilment.
        ...(deliveryPlan.fulfillment ? { fulfillment: deliveryPlan.fulfillment } : {}),
        timeType: draft.timeType,
        scheduledDate: draft.date,
        scheduledFor:
          draft.timeType !== "ASAP"
            ? localIsoFromDateTime(draft.date, draft.timeFrom)
            : undefined,
        scheduledUntil:
          draft.timeType === "WINDOW"
            ? localIsoFromDateTime(draft.date, draft.timeUntil)
            : undefined,
        totalEur:
          agreedTotal != null && Number.isFinite(agreedTotal) ? agreedTotal : undefined,
        bouquetPriceEur:
          bouquetPrice != null && Number.isFinite(bouquetPrice)
            ? bouquetPrice
            : undefined,
        lines,
      };
      // G-20: the deposit is captured at CREATION (a real payment row server-
      // side); edits do not retro-write money — corrections go through the
      // ledger, not a form resubmit.
      // G-112 (OD-100): "blank = full" means what the customer pays — the
      // goods AND the courier fee, which is owed through the same ledger. A
      // goods-only deposit would leave the fee owed and the courier refused
      // at pickup (OD-101).
      const deliveryFee =
        deliveryPlan.deliveryDetails && draft.deliveryPriceEur.trim() !== ""
          ? Number(draft.deliveryPriceEur)
          : 0;
      const customerPays =
        agreedTotal != null && Number.isFinite(deliveryFee)
          ? agreedTotal + deliveryFee
          : undefined;
      // G-166 (owner 2026-10-20): money is recorded only as a payment — with
      // no agreed price, the server refuses "already paid" without an amount
      // (its sentence is shown), before anything is written.
      // KI-7: "blank = full" only when there is money to record — a €0 price
      // and no fee leave the amount out, and the server asks for it.
      const depositFields = !editingOrder
        ? {
            prepaid: draft.prepaid,
            ...(draft.prepaid
              ? {
                  ...(draft.prepaidAmountEur.trim() !== ""
                    ? { prepaidAmountEur: Number(draft.prepaidAmountEur) }
                    : customerPays != null && customerPays > 0
                      ? { prepaidAmountEur: customerPays }
                      : {}),
                  prepaidMethod: draft.prepaidMethod,
                }
              : {}),
          }
        : {};
      order = editingOrder
        ? await api.updateCustomerOrder(editingOrder.id, shared)
        : await api.createCustomerOrder({
        locationId,
        source: draft.source,
        ...shared,
        fulfillment: draft.fulfillment,
        ...depositFields,
        confirmed: true,
      });

    } catch (err: unknown) {
      const short = parseShortages(err);
      if (short) {
        setShortages(short);
        // Shortages come from stock-linked lines: composed bouquet parts in
        // compose mode, retail items in describe mode.
        setStep(mode === "describe" ? "items" : "compose");
      } else {
        setSubmitError(err instanceof Error ? err.message : "Could not send the order.");
      }
      setSubmitting(false);
      return;
    }
    setShortages(null);

    // G-15 photo sync: delete what was removed, upload every draft. Failures
    // never roll the order back — the failed drafts stay for one-tap retry.
    // Photos sync concurrently — up to 10 uploads on a phone uplink is the
    // difference between a snappy send and a ten-round-trip wait. Failures
    // are tracked per file so only the failures are retried.
    const photoSync = await syncPhotos(order.id);
    const photoError = photoSync.error;

    // Delivery leg: the job carries the address, so COURIER orders always get
    // one. Failures here never roll back the order — the Dispatch page can
    // finish the leg later.
    let job: DeliveryJob | null = null;
    let jobError: string | null = null;
    let assignedCourierName: string | null = null;
    let assignError: string | null = null;
    if (deliveryPlan.updateJob || deliveryPlan.mayCreateJob) {
      const distanceKm = draft.distanceKm.trim() ? Number(draft.distanceKm) : undefined;
      try {
        job = deliveryPlan.updateJob && editingOrder?.deliveryJob
          ? await api.updateDeliveryJob(editingOrder.deliveryJob.id, {
              deliveryAddress: draft.deliveryAddress.trim(),
              recipientName: draft.recipientName.trim() || undefined,
              recipientPhone: draft.recipientPhone.trim() || undefined,
              recipientNotes: draft.recipientNotes.trim() || undefined,
              // G-177 (KI-42): price and distance are locked once a courier
              // holds the job — never re-sent (the server refused them).
              ...(deliveryPlan.courierLocked
                ? {}
                : {
                    priceEur: Number(draft.deliveryPriceEur),
                    distanceKm: Number.isFinite(distanceKm ?? NaN) ? distanceKm : undefined,
                  }),
              packageSize: draft.packageSize || undefined,
              scheduledPickupAt:
                draft.timeType !== "ASAP"
                  ? localIsoFromDateTime(draft.date, draft.timeFrom)
                  : undefined,
            })
          : await api.createDeliveryJob({
          orderId: order.id,
          deliveryAddress: draft.deliveryAddress.trim(),
          recipientName: draft.recipientName.trim() || undefined,
          recipientPhone: draft.recipientPhone.trim() || undefined,
          recipientNotes: draft.recipientNotes.trim() || undefined,
          priceEur: Number(draft.deliveryPriceEur),
          packageSize: draft.packageSize || undefined,
          distanceKm: Number.isFinite(distanceKm ?? NaN) ? distanceKm : undefined,
          scheduledPickupAt:
            draft.timeType !== "ASAP"
              ? localIsoFromDateTime(draft.date, draft.timeFrom)
              : undefined,
            });
      } catch (err: unknown) {
        jobError =
          err instanceof Error
            ? err.message
            : deliveryPlan.updateJob
              ? "Could not update the delivery job."
              : "Could not create the delivery job.";
      }
      if (job && assigningCourier) {
        try {
          await api.assignDeliveryJob(job.id, {
            courierUserId: draft.courierUserId,
            distanceKm: Number.isFinite(distanceKm ?? NaN) ? distanceKm : undefined,
            overridePayoutEur: draft.overridePayoutEur.trim()
              ? Number(draft.overridePayoutEur)
              : undefined,
          });
          assignedCourierName =
            couriers.find((c) => c.id === draft.courierUserId)?.fullName ?? "courier";
        } catch (err: unknown) {
          assignError =
            err instanceof Error ? err.message : "Could not assign the courier.";
        }
      }
    } else if (deliveryPlan.mayCancelJob && editingOrder?.deliveryJob) {
      try {
        await api.cancelDeliveryJob(editingOrder.deliveryJob.id, {
          reason: "Order changed to store pickup",
        });
      } catch (err: unknown) {
        jobError =
          err instanceof Error ? err.message : "Could not cancel the old delivery job.";
      }
    }

    setResult({
      order,
      job,
      jobError,
      assignedCourierName,
      assignError,
      photoError,
    });
    setStep("done");
    setSubmitting(false);
  };

  /**
   * Deletes removed photos and uploads new drafts, all concurrently, keeping
   * only the ones that actually failed so a retry re-sends just those.
   * Shared by submit and the retry button.
   */
  const syncPhotos = async (orderId: string): Promise<{ error: string | null }> => {
    let error: string | null = null;

    const deletions = await Promise.allSettled(
      removePhotoIds.map((photoId) =>
        api.deleteOrderReferencePhoto(orderId, photoId).then(() => photoId),
      ),
    );
    const failedDeletes = removePhotoIds.filter((_, i) => deletions[i].status === "rejected");
    if (failedDeletes.length > 0) {
      error = "Could not remove every reference photo.";
    }
    setRemovePhotoIds(failedDeletes);

    const uploads = await Promise.allSettled(
      photoDrafts.map((draft) => api.uploadOrderReferencePhoto(orderId, draft.file)),
    );
    const failedDrafts: PhotoDraft[] = [];
    photoDrafts.forEach((draft, i) => {
      const outcome = uploads[i];
      if (outcome.status === "fulfilled") {
        URL.revokeObjectURL(draft.previewUrl);
        return;
      }
      failedDrafts.push(draft);
      error =
        outcome.reason instanceof Error
          ? outcome.reason.message
          : "Could not save a reference photo.";
    });
    setPhotoDrafts(failedDrafts);

    return { error };
  };

  /** Re-attempts only the drafts that failed to upload on submit. */
  const retryPhotoUploads = async () => {
    if (!result || photoDrafts.length === 0) return;
    setSubmitting(true);
    const { error } = await syncPhotos(result.order.id);
    setResult({ ...result, photoError: error });
    setSubmitting(false);
  };

  const startAnother = () => {
    setCart(EMPTY_CART);
    setRetailLines([]);
    setDraft(emptyCheckoutDraft(orderDayKey(new Date())));
    photoDrafts.forEach((draftPhoto) => URL.revokeObjectURL(draftPhoto.previewUrl));
    setPhotoDrafts([]);
    setExistingPhotos([]);
    setRemovePhotoIds([]);
    setShortages(null);
    setSubmitError(null);
    setResult(null);
    // Back to the default rather than to a chooser screen that no longer exists.
    setMode("describe");
    setStep("compose");
    void loadCatalog();
  };

  if (!user) return null;

  const suggested = cartSuggestedPrice(cart);
  const agreedTotal = goodsTotal(cart.bouquetPriceEur, retailLines, heldBouquetsEur);
  const itemsSummary =
    mode === "describe"
      ? `Described bouquet · ${describePhotos.length} photo${
          describePhotos.length === 1 ? "" : "s"
        } for ${locationName}`
      : `${cartStemCount(cart)} stems · ${
          cart.flowers.length + cart.papers.length
        } items for ${locationName}`;

  // Checkout speaks for itself (its own labelled fields) — no subtitle there.
  const stepSubtitle =
    step === "compose"
      ? mode === "describe"
        ? "Describe the bouquet — the florist composes it in the shop."
        : `Compose a bouquet from ${locationName}'s stock, then add the customer.`
      : step === "items"
        ? "Add any separately sold shop items, then review and send."
        : step === "done"
          ? editingOrder
            ? "Order updated."
            : "Order sent."
          : "";

  if (editLoading && !editingOrder) {
    return <div className="empty-state">Loading order…</div>;
  }

  return (
    <section className="pob-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">
            {editingOrder ? `Edit order ${editingOrder.code}` : "Build & send order"}
          </h2>
          {stepSubtitle ? <p className="page-subtitle">{stepSubtitle}</p> : null}
        </div>
        <Link to="/app/orders" className="button plain">
          Orders
        </Link>
      </div>

      {step !== "done" ? (
        <div className="pob-progress" aria-label="Order progress">
          <span className={step === "compose" ? "is-active" : "is-complete"}>
            <b>1</b> Bouquet
          </span>
          <span className={step === "items" ? "is-active" : step === "checkout" ? "is-complete" : ""}>
            <b>2</b> Items
          </span>
          <span className={step === "checkout" ? "is-active" : ""}>
            <b>3</b> Customer &amp; delivery
          </span>
        </div>
      ) : null}

      {step === "compose" ? (
        <>
          {ownerScoped ? (
            <div className="field pob-location-field">
              <label htmlFor="pob-location">Shop</label>
              <select
                id="pob-location"
                value={locationId}
                onChange={(e) => changeLocation(e.target.value)}
                disabled={loading || Boolean(editingOrder)}
              >
                <option value="">Choose a shop…</option>
                {activeLocations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {loadError ? (
            <div className="alert error">
              {loadError}{" "}
              <button type="button" className="button plain" onClick={() => void loadCatalog()}>
                Retry
              </button>
            </div>
          ) : null}

          {locationId ? (
            <div
              className="pob-mode-switch"
              role="group"
              aria-label="How this bouquet is decided"
            >
              <button
                type="button"
                aria-pressed={mode === "describe"}
                onClick={() => setMode("describe")}
              >
                Describe it
              </button>
              <button
                type="button"
                aria-pressed={mode === "compose"}
                onClick={() => setMode("compose")}
              >
                Exact stems
              </button>
            </div>
          ) : null}

          {!locationId ? (
            <div className="empty-state">
              You need an assigned shop to send orders. Ask the owner to assign you.
            </div>
          ) : mode === "describe" ? (
            <>
              {cart.flowers.length + cart.papers.length > 0 ? (
                <div className="alert warning pob-stems-parked">
                  {cart.flowers.length + cart.papers.length === 1
                    ? "1 picked piece is set aside while you describe this one — it is not sent."
                    : `${cart.flowers.length + cart.papers.length} picked pieces are set aside while you describe this one — they are not sent.`}{" "}
                  Switch back to <strong>Exact stems</strong> to use them.
                </div>
              ) : null}
              <DescribeBouquet
              cart={cart}
              onCartChange={setCart}
              customerNote={draft.customerNotes}
              onCustomerNoteChange={(next) =>
                setDraft((cur) => ({ ...cur, customerNotes: next }))
              }
              photos={describePhotos}
              photoCap={REFERENCE_PHOTO_CAP}
              onAddFiles={(files) => void addPhotoFiles(files)}
              onRemovePhoto={removePhoto}
              error={submitError}
              onContinue={() => {
                if (!cart.bouquetNotes.trim()) {
                  setSubmitError(
                    "Describe the bouquet first — that text is what the florist builds from.",
                  );
                  return;
                }
                setSubmitError(null);
                setStep("items");
              }}
              />
            </>
          ) : (
            <>
              <div className="pob-stock-actions">
                <span>
                  Live stock for <strong>{locationName}</strong>
                </span>
                <button
                  type="button"
                  className="button tinted"
                  onClick={() => void loadCatalog()}
                  disabled={loading}
                >
                  {loading ? "Refreshing…" : "Refresh stock"}
                </button>
              </div>

              {shortages ? (
                <div className="alert error">
                  <strong>{locationName} does not have enough stock.</strong>
                  <ul className="pob-shortage-list">
                    {shortages.map((s) => (
                      <li key={s.itemId}>
                        {s.name} — need {s.requested}, have {s.available}
                      </li>
                    ))}
                  </ul>
                  Adjust the bouquet and send again.
                </div>
              ) : null}

              {loading ? (
                <p className="muted">Loading {locationName}&apos;s stock…</p>
              ) : (
                <PhoneBouquetComposer
                  flowers={flowers}
                  colors={colors}
                  subcategories={subcategories}
                  rolls={rolls}
                  cart={cart}
                  onCartChange={setCart}
                  shortages={shortages}
                  referencePreviewUrl={
                    describePhotos.length > 0
                      ? describePhotos[describePhotos.length - 1].previewUrl
                      : null
                  }
                  onReferenceFile={(file) => void addPhotoFiles([file])}
                  onRemoveReference={() => {
                    const last = describePhotos[describePhotos.length - 1];
                    if (last) removePhoto(last.key);
                  }}
                  onSkip={() => {
                    setCart(EMPTY_CART);
                    clearAllPhotos();
                    setSubmitError(null);
                    setStep("items");
                  }}
                  onContinue={() => {
                    setSubmitError(null);
                    setStep("items");
                  }}
                />
              )}
            </>
          )}
        </>
      ) : null}

      {step === "checkout" ? (
        <PhoneOrderCheckout
          draft={draft}
          onChange={setDraft}
          couriers={couriers}
          couriersLoading={couriersLoading}
          payoutPreview={payoutPreview}
          payoutPreviewLoading={payoutPreviewLoading}
          payoutPreviewError={payoutPreviewError}
          itemsSummary={itemsSummary}
          agreedTotal={agreedTotal != null && Number.isFinite(agreedTotal) ? agreedTotal : null}
          lockedSourceLabel={editingOrder ? orderSourceLabel(editingOrder.source) : null}
          submitting={submitting}
          error={submitError}
          onBack={() => {
            setSubmitError(null);
            setStep("items");
          }}
          onSubmit={() => void submit()}
          submitLabel={editingOrder ? "Update order" : "Send to shop"}
          showPaid={!editingOrder}
          deliveryPlan={deliveryPlan}
          assignedCourierName={editingOrder?.deliveryJob?.courier?.fullName ?? null}
          closedJobLabel={
            deliveryPlan.jobClosed && editingOrder?.deliveryJob
              ? `${editingOrder.deliveryJob.code} · ${deliveryJobStatusLabel(t, editingOrder.deliveryJob.status)}`
              : null
          }
        />
      ) : null}

      {step === "items" ? (
        <PhoneOrderItems
          items={orderableItems}
          colors={colors}
          lines={retailLines}
          onChange={setRetailLines}
          bouquet={cart}
          referencePhotoCount={describePhotos.length}
          submitting={submitting}
          error={submitError}
          submitLabel="Continue to customer & delivery"
          totalEur={cart.bouquetPriceEur}
          onTotalEurChange={(value) =>
            setCart((current) => ({ ...current, bouquetPriceEur: value }))
          }
          onBack={() => {
            setSubmitError(null);
            setStep("compose");
          }}
          onSubmit={() => {
            setSubmitError(null);
            setStep("checkout");
          }}
        />
      ) : null}

      {step === "done" && result ? (
        <div className="card pob-success">
          <h3>
            Order {result.order.code} {editingOrder ? "updated" : `sent to ${locationName}`}
          </h3>
          <p className="muted">
            The shop sees it on today&apos;s list. Stock was available when sent — the
            florist builds it on the floor.
          </p>
          <ul className="pob-success-facts">
            <li>
              {fulfillmentLabel(t, result.order.fulfillment)} ·{" "}
              {result.order.scheduledDate.slice(0, 10)}
            </li>
            {agreedTotal != null && Number.isFinite(agreedTotal) ? (
              <li>Agreed total {eur(agreedTotal)}</li>
            ) : suggested.totalEur > 0 ? (
              <li>Suggested price {eur(suggested.totalEur)} (not agreed)</li>
            ) : null}
            {result.job ? (
              <li>
                Delivery job {result.job.code} {deliveryPlan.updateJob ? "updated" : "created"}
              </li>
            ) : null}
            {result.assignedCourierName ? (
              <li>Assigned to {result.assignedCourierName}</li>
            ) : null}
          </ul>
          {result.jobError ? (
            <div className="alert error">
              {deliveryPlan.updateJob
                ? `The order was saved, but its delivery job ${editingOrder?.deliveryJob?.code ?? ""} was not updated: ${result.jobError}. Change it on the Courier board.`
                : `The order was sent, but the delivery job failed: ${result.jobError}. Create it from the Courier board.`}
            </div>
          ) : null}
          {result.photoError ? (
            <div className="alert error">
              The order was saved, but not every reference photo was:{" "}
              {result.photoError}
              {photoDrafts.length > 0 ? (
                <button
                  type="button"
                  className="button tinted"
                  onClick={() => void retryPhotoUploads()}
                  disabled={submitting}
                >
                  {submitting
                    ? "Retrying…"
                    : `Retry ${photoDrafts.length} photo upload${photoDrafts.length === 1 ? "" : "s"}`}
                </button>
              ) : null}
            </div>
          ) : null}
          {result.assignError ? (
            <div className="alert error">
              Delivery job {result.job?.code} created, but courier assignment failed:{" "}
              {result.assignError}. Assign from the Courier board.
            </div>
          ) : null}
          <div className="button-row">
            {!editingOrder ? (
              <button type="button" className="button" onClick={startAnother}>
                Build another
              </button>
            ) : null}
            <Link
              to={`/app/orders?locationId=${encodeURIComponent(locationId)}`}
              className="button tinted"
            >
              View orders
            </Link>
            {result.job ? (
              <Link to="/app/dispatch" className="button tinted">
                Courier board
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
